import React, { useState } from 'react';
import PaperContainer from 'ui/PaperContainer';
import Fields from 'ui/form/FieldsForm';
import { CircularProgress } from '@material-ui/core';
import {
    TwoFactorForm,
    PasswordDialog,
    SetupDialog,
    RecoveryCodesDialog,
    BackupPhoneDialog,
} from './components';
import { ACTIONS } from './const';
import { loadConfig, disable2FA, loadRecoveryCodes } from '../service';
import { useOnMount } from 'utils/react';

const Authentication = ({ extra: { currentUser } }) => {
    const [currentAction, setCurrentAction] = useState({});
    const [setup, setSetup] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});

    const handle2FADisable = async data => {
        await disable2FA({ confirmCode: data.confirm_code });
        setData({ enabled: false });
    };

    const handleChangeEnabled = value => {
        if (value) {
            setSetup(true);
            setCurrentAction({
                type: ACTIONS.DIALOG_CONFIRM,
                payload: { next: ACTIONS.DIALOG_SETUP },
            });
        } else {
            setCurrentAction({
                type: ACTIONS.DIALOG_CONFIRM,
                payload: { callback: handle2FADisable },
            });
        }
    };

    const handleDataUpdate = value => setData({ ...data, ...value });

    const handleAction = async (type, payload, actionData) => {
        switch (type) {
            case ACTIONS.CONFIRM:
                let codes;
                if (payload.next === ACTIONS.DIALOG_RECOVERY_CODES) {
                    codes = await loadRecoveryCodes({
                        confirmCode: payload.confirm_code,
                    });
                }
                if (payload.callback) {
                    await payload.callback({
                        confirm_code: payload.confirm_code,
                        ...codes,
                    });
                    setCurrentAction({});
                } else {
                    setCurrentAction({
                        type: payload.next,
                        payload: {
                            confirm_code: payload.confirm_code,
                            ...codes,
                        },
                    });
                }
                break;
            case ACTIONS.SETUP:
                setCurrentAction({
                    type: ACTIONS.DIALOG_RECOVERY_CODES,
                    payload,
                });
                break;
            case ACTIONS.BACKUP_PHONE:
                setCurrentAction({
                    type: ACTIONS.DIALOG_BACKUP_PHONE,
                    payload,
                });
                break;
            case ACTIONS.CLOSE:
                setCurrentAction({});
                break;
            default:
                break;
        }
    };

    const handleFormAction = type => {
        let nextDialog;
        switch (type) {
            case 'method':
                setSetup(true);
                nextDialog = ACTIONS.DIALOG_SETUP;
                break;
            case 'backup_phone':
                setSetup(false);
                nextDialog = ACTIONS.DIALOG_BACKUP_PHONE;
                break;
            case 'recovery_codes':
                setSetup(false);
                nextDialog = ACTIONS.DIALOG_RECOVERY_CODES;
                break;
            default:
                break;
        }
        if (nextDialog) {
            setCurrentAction({
                type: ACTIONS.DIALOG_CONFIRM,
                payload: { next: nextDialog },
            });
        }
    };

    const loadData = async () => {
        const config = await loadConfig();
        setData(config);
    };

    useOnMount(async () => {
        setLoading(true);
        await loadData();
        setLoading(false);
    });

    return (
        <>
            {currentAction.type === ACTIONS.DIALOG_CONFIRM && (
                <PasswordDialog
                    open
                    currentUser={currentUser}
                    onSubmit={confirm_code =>
                        handleAction(ACTIONS.CONFIRM, {
                            ...currentAction.payload,
                            confirm_code,
                        })
                    }
                    onCancel={() => handleAction(ACTIONS.CLOSE)}
                />
            )}
            {currentAction.type === ACTIONS.DIALOG_SETUP && (
                <SetupDialog
                    open
                    onUpdate={handleDataUpdate}
                    data={currentAction.payload}
                    onCancel={() => handleAction(ACTIONS.CLOSE)}
                    onSubmit={payload => handleAction(ACTIONS.SETUP, payload)}
                />
            )}
            {currentAction.type === ACTIONS.DIALOG_RECOVERY_CODES && (
                <RecoveryCodesDialog
                    open
                    setup={setup}
                    data={currentAction.payload}
                    onSubmit={payload =>
                        handleAction(
                            setup ? ACTIONS.BACKUP_PHONE : ACTIONS.CLOSE,
                            payload
                        )
                    }
                />
            )}
            {currentAction.type === ACTIONS.DIALOG_BACKUP_PHONE && (
                <BackupPhoneDialog
                    open
                    value={data.backup_phone}
                    setup={setup}
                    data={currentAction.payload}
                    onUpdate={handleDataUpdate}
                    onSubmit={() => handleAction(ACTIONS.CLOSE)}
                />
            )}
            <PaperContainer
                title="Two-Factor Authentication"
                subtitle="Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to log in."
                secondary
            >
                {loading && <CircularProgress />}
                {!loading && (
                    <Fields.Row>
                        <Fields.Switch
                            value={data.enabled}
                            onChange={handleChangeEnabled}
                            label={data.enabled ? 'Enabled' : 'Disabled'}
                        />
                    </Fields.Row>
                )}
                {data.enabled && !loading && (
                    <TwoFactorForm data={data} onAction={handleFormAction} />
                )}
            </PaperContainer>
        </>
    );
};

export { Authentication };
